// =============================================================================
// screens/CreateCommunityScreen.tsx — Create Community (modal) as a CUSTOMIZATION
// STUDIO.
//
// MIGRATION (bare RN, P3 wave-2 MISC SOCIAL): ported off apps/cryb-native
// app/create-community.tsx. Swaps: expo-router → react-navigation (useRouter →
// useNavigation; router.back() → navigation.goBack(); Stack.Screen header is
// registered in App.tsx); @expo/vector-icons → Icon shim. Services/theme import
// paths unchanged. POST /communities contract is preserved exactly.
//
// REDESIGN (client: "set color cards, names, and pictures"): the flat form is
// now a customization studio. A LIVE CoinCommunityCard preview sits at the top
// and re-renders as the user types a name, sets a picture, or picks a color —
// the exact card that will appear in the directory (§16 grouping: each control
// sits right under the thing it changes; §12: full-bleed ParticleBackground
// under the App.tsx translucent header, matching CommunitiesScreen).
//
// Picture: reuses EditProfileScreen's exact launchImageLibrary → uploadMedia
// pipeline (local preview first; upload deferred to save; failed upload ABORTS
// the whole create so we never leave a half-built community — §16 Responsibility).
// Color: no server column yet, so it's persisted DEVICE-LOCAL via AsyncStorage
// and honestly labelled "saved on this device — cross-device sync coming soon".
// =============================================================================
import { useState, useEffect, useCallback, useLayoutEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  Image,
  Pressable,
  Alert,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Switch,
  useWindowDimensions,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { launchImageLibrary } from 'react-native-image-picker';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Ionicons } from '../components/ui/Icon';
import {
  colors, spacing, radius, fonts, getLayout, layout as layoutTokens,
  typographyScale,
} from '../constants/theme';
import { GlassCard, GlassButton, GlassSurface } from '../components/glass';
import ParticleBackground from '../components/ui/ParticleBackground';
import { CoinCommunityCard } from '../components/discover';
import { api, uploadMedia, getLastUploadError } from '../services/api';

// Local mirror of CoinCommunityCard's SOLID palette + deterministic hash so the
// swatch strip and the "Auto" default match the directory card exactly. (Kept
// in sync with components/discover/CoinCommunityCard.tsx SOLIDS.)
const SOLIDS = [
  '#1B1636', // deep indigo-ink
  '#3A2C6E', // deep purple
  '#4B2A7B', // violet
  '#5A2A67', // plum
  '#262150', // slate purple
  '#6C3FA0', // CRYB purple
  '#7A3579', // orchid
  '#233A6B', // deep blue
];

function solidFor(key: string): string {
  let h = 0;
  const s = String(key || '');
  for (let i = 0; i < s.length; i++) h = (h * 33 + s.charCodeAt(i)) >>> 0;
  return SOLIDS[h % SOLIDS.length];
}

function slugify(name: string): string {
  // Server regex is ^[a-zA-Z0-9_]+$ (no hyphens), max 21 chars.
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .slice(0, 21);
}

// ── Creation eligibility gate (client-side) ─────────────────────────────────
// The backend has NO per-user gate on POST /communities (only a global
// site-setting kill switch). Per the client ask ("not everyone should be able
// to create"), we gate the FORM behind a clear eligibility flow instead of an
// open create. Criteria are OR'd: a VERIFIED account, OR an account at least
// MIN_ACCOUNT_AGE_DAYS old. If neither is met we show a "how to unlock" state
// and a request-access path — we never fake success or bypass the check.
const MIN_ACCOUNT_AGE_DAYS = 7;

function daysSince(iso?: string | null): number {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return 0;
  return Math.max(0, Math.floor((Date.now() - t) / 86400000));
}

export default function CreateCommunityScreen() {
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const containerStyle = layout !== 'phone' ? { maxWidth: layout === 'desktop' ? 720 : 620, alignSelf: 'center' as const, width: '100%' as const } : undefined;

  // §12 Translucent chrome: make THIS modal's header transparent so the
  // full-bleed ParticleBackground shows through and content scrolls under it
  // (the shared App.tsx headerStyle is opaque colors.bg — override locally).
  useLayoutEffect(() => {
    navigation.setOptions({
      headerTransparent: true,
      headerStyle: { backgroundColor: 'transparent' },
      headerShadowVisible: false,
      headerTintColor: colors.primary,
      headerTitleStyle: { ...fonts.semibold, fontSize: 17, color: colors.ink },
    });
  }, [navigation]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [rules, setRules] = useState('');
  const [tokenGating, setTokenGating] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Char-counter visibility: only surface the count while the field is in play.
  const [descFocused, setDescFocused] = useState(false);
  const [rulesFocused, setRulesFocused] = useState(false);
  // Customization state (client ask): local image preview + chosen card color.
  const [iconLocal, setIconLocal] = useState<string | null>(null);
  // accent === null → follow the deterministic solidFor(slug) ("Auto").
  const [accent, setAccent] = useState<string | null>(null);

  // ── Eligibility gate state ──
  // 'checking' until /auth/me resolves; then 'ok' (show form) or 'locked'.
  const [gate, setGate] = useState<'checking' | 'ok' | 'locked'>('checking');
  const [isVerified, setIsVerified] = useState(false);
  const [accountAgeDays, setAccountAgeDays] = useState(0);
  // A device-local flag: the user has requested access (honest — this records
  // the request locally and tells them it's under review; it does NOT unlock
  // creation, because there is no server approval endpoint yet).
  const [accessRequested, setAccessRequested] = useState(false);

  const evaluateEligibility = useCallback(async () => {
    setGate('checking');
    try {
      // /auth/me → { data: { user: { isVerified, createdAt, ... } } }.
      const res = await api<{ user?: any }>('/auth/me');
      const u = res.success ? res.data?.user : null;
      const verified = !!u?.isVerified;
      const ageDays = daysSince(u?.createdAt);
      setIsVerified(verified);
      setAccountAgeDays(ageDays);
      // Restore any prior device-local access request for this account.
      try {
        const flag = await AsyncStorage.getItem(`communityCreateRequested:${u?.id ?? 'me'}`);
        setAccessRequested(flag === '1');
      } catch { /* best-effort */ }
      const eligible = verified || ageDays >= MIN_ACCOUNT_AGE_DAYS;
      setGate(eligible ? 'ok' : 'locked');
    } catch {
      // On a failed identity fetch, fail CLOSED (locked) — never open the form to
      // an unverified/unknown session. The user can retry.
      setGate('locked');
    }
  }, []);

  useEffect(() => { evaluateEligibility(); }, [evaluateEligibility]);

  // Record a device-local access request (honest: no server approval endpoint,
  // so this does not unlock — it acknowledges the request and sets expectations).
  async function requestAccess() {
    setAccessRequested(true);
    try {
      const res = await api<{ user?: any }>('/auth/me');
      const uid = res.success ? res.data?.user?.id : null;
      await AsyncStorage.setItem(`communityCreateRequested:${uid ?? 'me'}`, '1');
    } catch { /* best-effort persistence */ }
  }

  const slug = slugify(name);
  // Preview always resolves to a concrete color; null accent tracks the name.
  const effectiveColor = accent ?? solidFor(slug);

  const canSubmit = name.trim().length >= 3 && description.trim().length > 0;

  // Open the photo library (same flow as EditProfileScreen). Local preview only;
  // the real upload is deferred to handleCreate so a cancelled create uploads
  // nothing.
  async function pickImage() {
    try {
      const result = await launchImageLibrary({
        mediaType: 'photo',
        selectionLimit: 1,
        quality: 0.9,
        includeBase64: false,
      });
      if (result.didCancel) return;
      if (result.errorCode) {
        Alert.alert('Error', result.errorMessage || 'Could not open the photo library. Please try again.');
        return;
      }
      const uri = result.assets?.[0]?.uri;
      if (uri) setIconLocal(uri);
    } catch {
      Alert.alert('Error', 'Could not open the photo library. Please try again.');
    }
  }

  async function handleCreate() {
    if (!canSubmit) return;
    setSubmitting(true);

    const rulesArray = rules
      .split('\n')
      .map((r) => r.trim())
      .filter((r) => r.length > 0);

    const body: Record<string, any> = {
      name: slug,
      displayName: name.trim(),
      description: description.trim(),
      tokenGating,
    };
    // Schema wants {title,description}[]; omit entirely when empty.
    if (rulesArray.length > 0) {
      body.rules = rulesArray.map((r) => ({ title: r, description: '' }));
    }

    // Upload the picked picture first. Abort the whole create on failure so we
    // never persist a community that's missing the image the owner chose
    // (§16 Responsibility — no half-built state).
    if (iconLocal) {
      const url = await uploadMedia(iconLocal);
      if (!url) {
        Alert.alert('Upload Failed', getLastUploadError() || 'Could not upload the community picture. Please try again.');
        setSubmitting(false);
        return;
      }
      body.icon = url;
    }

    const res = await api('/communities', {
      method: 'POST',
      body: JSON.stringify(body),
    });

    setSubmitting(false);

    if (res.success) {
      // No server color column yet — persist the chosen card color device-local
      // so the directory/detail can read it back. Non-fatal if it fails.
      try {
        await AsyncStorage.setItem(`communityColor:${slug}`, effectiveColor);
      } catch { /* device-local persistence is best-effort */ }
      Alert.alert('Success', 'Community created!', [
        { text: 'OK', onPress: () => navigation.goBack() },
      ]);
    } else {
      Alert.alert('Error', res.error || 'Failed to create community');
    }
  }

  // ── Gate: checking identity ──
  if (gate === 'checking') {
    return (
      <View style={styles.container}>
        <ParticleBackground />
        <View style={[styles.centerFill, { paddingTop: insets.top + layoutTokens.headerHeight }]}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.gateCheckingText}>Checking your account…</Text>
        </View>
      </View>
    );
  }

  // ── Gate: not eligible → "how to unlock creating" state (no open create) ──
  if (gate === 'locked') {
    const daysLeft = Math.max(0, MIN_ACCOUNT_AGE_DAYS - accountAgeDays);
    return (
      <View style={styles.container}>
        <ParticleBackground />
        <ScrollView
          contentContainerStyle={[
            styles.scrollContent,
            containerStyle,
            {
              paddingTop: insets.top + layoutTokens.headerHeight + spacing.lg,
              paddingBottom: insets.bottom + spacing.xxl,
            },
          ]}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.gateHeader}>
            <View style={styles.gateLockCircle}>
              <Ionicons name="lock-closed" size={26} color={colors.primary} />
            </View>
            <Text style={styles.gateTitle}>Creating is unlocked as you grow</Text>
            <Text style={styles.gateSub}>
              To keep communities high-quality and spam-free, creating one is
              limited to established members. Meet either requirement below.
            </Text>
          </View>

          {/* Criteria checklist — each row shows met/unmet honestly. */}
          <GlassCard style={styles.gateCard} padding={spacing.lg}>
            <CriterionRow
              met={isVerified}
              title="Verify your account"
              detail={isVerified ? 'Your account is verified.' : 'Verify your email to unlock instantly.'}
            />
            <View style={styles.gateOrRow}>
              <View style={styles.gateOrLine} />
              <Text style={styles.gateOrText}>OR</Text>
              <View style={styles.gateOrLine} />
            </View>
            <CriterionRow
              met={accountAgeDays >= MIN_ACCOUNT_AGE_DAYS}
              title={`Be a member for ${MIN_ACCOUNT_AGE_DAYS} days`}
              detail={
                accountAgeDays >= MIN_ACCOUNT_AGE_DAYS
                  ? 'You meet the account-age requirement.'
                  : `You're ${accountAgeDays} day${accountAgeDays === 1 ? '' : 's'} in — ${daysLeft} to go.`
              }
            />
          </GlassCard>

          {/* Actions: verify now (real screen) + a request-access acknowledgement. */}
          <View style={styles.gateActions}>
            {!isVerified && (
              <GlassButton
                title="Verify my account"
                icon="shield-checkmark-outline"
                onPress={() => {
                  // Route to the profile where verification lives; fall back to a
                  // graceful back-out if the route isn't available.
                  try { (navigation as any).navigate('EditProfile'); }
                  catch { navigation.goBack(); }
                }}
                fullWidth
                size="lg"
              />
            )}
            {accessRequested ? (
              <GlassCard style={styles.gateRequestedCard} padding={spacing.lg} contentStyle={styles.gateRequestedRow}>
                <Ionicons name="time-outline" size={18} color={colors.primary} />
                <Text style={styles.gateRequestedText}>
                  Request received — we'll notify you when creating is unlocked.
                </Text>
              </GlassCard>
            ) : (
              <GlassButton
                title="Request early access"
                icon="paper-plane-outline"
                variant="secondary"
                onPress={requestAccess}
                fullWidth
                size="lg"
              />
            )}
            <GlassButton
              title="Recheck"
              icon="refresh-outline"
              variant="ghost"
              onPress={evaluateEligibility}
              fullWidth
            />
          </View>
        </ScrollView>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <ParticleBackground />
      <ScrollView
        contentContainerStyle={[
          styles.scrollContent,
          containerStyle,
          {
            // Clear the translucent nav bar (content scrolls under it)…
            paddingTop: insets.top + layoutTokens.headerHeight + spacing.md,
            // …and clear the pinned frosted commit footer at the bottom.
            paddingBottom: 54 + insets.bottom + spacing.xxl,
          },
        ]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {/* Intro — tightened to a single left-aligned line (§16 concise);
            the live preview below does the heavy lifting, not marketing copy. */}
        <View style={styles.intro}>
          <View style={styles.introIcon}>
            <Ionicons name="people" size={22} color={colors.primary} />
          </View>
          <Text style={styles.introTitle}>Start a community</Text>
          <Text style={styles.introSub}>Name it, describe it, set the rules.</Text>
        </View>

        {/* Live preview — the real directory card, re-tinting as the user types,
            picks a picture, or chooses a color. Tapping it opens the picker. */}
        <Text style={styles.previewLabel}>PREVIEW</Text>
        <View style={styles.previewWrap}>
          <CoinCommunityCard
            community={{
              id: 'preview',
              name: slug || 'yourcommunity',
              displayName: name.trim() || 'Your Community',
              description: description.trim(),
              avatar: iconLocal || undefined,
              memberCount: 1,
            }}
            // `color` overrides CoinCommunityCard's deterministic hash so the
            // owner's pick shows live. Requires the backward-compatible prop add
            // (see sharedChangesNeeded); cast keeps this file type-safe today.
            {...({ color: effectiveColor } as any)}
            onPress={pickImage}
            fullWidth
          />
        </View>

        {/* Name */}
        <Text style={styles.label}>Community Name</Text>
        <GlassSurface variant="bar" radius={radius.lg} contentStyle={styles.inputContent}>
          <TextInput
            style={styles.input}
            placeholder="e.g. Bitcoin Traders"
            placeholderTextColor={colors.muted}
            value={name}
            onChangeText={setName}
            maxLength={50}
          />
        </GlassSurface>
        {name.trim().length > 0 && (
          <Text style={styles.slugPreview}>c/{slug}</Text>
        )}

        {/* Picture */}
        <Text style={styles.label}>Community Picture</Text>
        <View style={styles.pictureRow}>
          <Pressable onPress={pickImage} style={styles.thumbPress} accessibilityRole="button" accessibilityLabel="Community picture">
            <GlassSurface variant="card" radius={radius.lg} contentStyle={styles.thumbContent}>
              {iconLocal ? (
                <Image source={{ uri: iconLocal }} style={styles.thumbImg} />
              ) : (
                <Ionicons name="image-outline" size={22} color={colors.muted} />
              )}
            </GlassSurface>
          </Pressable>
          <View style={styles.pictureActions}>
            <GlassButton
              title={iconLocal ? 'Change photo' : 'Add photo'}
              icon="camera-outline"
              variant="secondary"
              size="sm"
              onPress={pickImage}
            />
            {iconLocal && (
              <Pressable onPress={() => setIconLocal(null)} hitSlop={8}>
                <Text style={styles.removeText}>Remove</Text>
              </Pressable>
            )}
          </View>
        </View>

        {/* Color */}
        <Text style={styles.label}>Card Color</Text>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.swatchStrip}
        >
          {/* "Auto" — deterministic solidFor(slug), the default. */}
          <Swatch
            color={solidFor(slug)}
            selected={accent === null}
            onPress={() => setAccent(null)}
            label="Auto"
          />
          {SOLIDS.map((c) => (
            <Swatch
              key={c}
              color={c}
              selected={accent === c}
              onPress={() => setAccent(c)}
            />
          ))}
        </ScrollView>
        <Text style={styles.colorNote}>
          Color is saved on this device — cross-device sync coming soon.
        </Text>

        {/* Description */}
        <Text style={styles.label}>Description</Text>
        <GlassSurface variant="bar" radius={radius.lg} contentStyle={styles.inputContent}>
          <TextInput
            style={[styles.input, styles.textArea]}
            placeholder="What is this community about?"
            placeholderTextColor={colors.muted}
            multiline
            maxLength={500}
            value={description}
            onChangeText={setDescription}
            textAlignVertical="top"
            onFocus={() => setDescFocused(true)}
            onBlur={() => setDescFocused(false)}
          />
        </GlassSurface>
        {(descFocused || description.length > 0) && (
          <Text style={styles.counter}>{description.length}/500</Text>
        )}

        {/* Rules */}
        <Text style={styles.label}>Rules (one per line)</Text>
        <GlassSurface variant="bar" radius={radius.lg} contentStyle={styles.inputContent}>
          <TextInput
            style={[styles.input, styles.textArea]}
            placeholder={'Be respectful\nNo spam\nNo financial advice'}
            placeholderTextColor={colors.muted}
            multiline
            maxLength={1000}
            value={rules}
            onChangeText={setRules}
            textAlignVertical="top"
            onFocus={() => setRulesFocused(true)}
            onBlur={() => setRulesFocused(false)}
          />
        </GlassSurface>
        {(rulesFocused || rules.length > 0) && (
          <Text style={styles.counter}>{rules.length}/1000</Text>
        )}

        {/* Token Gating */}
        <GlassCard style={styles.toggleCard} padding={spacing.lg} contentStyle={styles.toggleRow}>
          <View style={styles.toggleInfo}>
            <View style={styles.toggleIcon}>
              <Ionicons name="lock-closed-outline" size={18} color={colors.primary} />
            </View>
            <View style={styles.toggleTextWrap}>
              <Text style={styles.toggleTitle}>Token Gating</Text>
              <Text style={styles.toggleDesc}>
                Require members to hold a specific token to join
              </Text>
            </View>
          </View>
          <Switch
            value={tokenGating}
            onValueChange={setTokenGating}
            trackColor={{ false: colors.faint, true: colors.primary + '60' }}
            thumbColor={tokenGating ? colors.primary : colors.white}
          />
        </GlassCard>

      </ScrollView>

      {/* §12/§16 Persistent frosted commit bar — content scrolls under it, and
          the primary action is always reachable in the modal. */}
      <GlassSurface
        variant="bar"
        radius={0}
        style={styles.footer}
        contentStyle={[
          styles.footerContent,
          containerStyle,
          { paddingBottom: insets.bottom + spacing.md },
        ]}
      >
        <GlassButton
          title="Create Community"
          icon="add-circle-outline"
          onPress={handleCreate}
          loading={submitting}
          disabled={!canSubmit || submitting}
          fullWidth
          size="lg"
        />
      </GlassSurface>
    </KeyboardAvoidingView>
  );
}

// One eligibility criterion row: a met/unmet check chip + title + detail.
function CriterionRow({ met, title, detail }: { met: boolean; title: string; detail: string }) {
  return (
    <View style={styles.criterionRow}>
      <View style={[styles.criterionCheck, met ? styles.criterionCheckMet : styles.criterionCheckUnmet]}>
        <Ionicons name={met ? 'checkmark' : 'ellipse-outline'} size={15} color={met ? '#FFFFFF' : colors.muted} />
      </View>
      <View style={styles.criterionTextWrap}>
        <Text style={[styles.criterionTitle, met && styles.criterionTitleMet]}>{title}</Text>
        <Text style={styles.criterionDetail}>{detail}</Text>
      </View>
    </View>
  );
}

// A single color swatch. Press-down scales to 0.92 (§1 respond-on-press);
// selected gets a white ring + centered checkmark.
function Swatch({
  color, selected, onPress, label,
}: { color: string; selected: boolean; onPress: () => void; label?: string }) {
  return (
    <View style={styles.swatchCol}>
      <Pressable
        onPress={onPress}
        accessibilityRole="button"
        accessibilityState={{ selected }}
        accessibilityLabel={label ? `${label} color` : 'Card color'}
        style={({ pressed }) => [
          styles.swatch,
          { backgroundColor: color },
          selected && styles.swatchSelected,
          pressed && { transform: [{ scale: 0.92 }] },
        ]}
      >
        {selected && <Ionicons name="checkmark" size={16} color="#FFFFFF" />}
      </Pressable>
      {label && <Text style={styles.swatchLabel}>{label}</Text>}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    // §12: transparent so the full-bleed ParticleBackground (behind) shows and
    // content scrolls under the translucent header. Vertical padding is applied
    // dynamically on scrollContent (header + footer clearance).
    backgroundColor: 'transparent',
  },
  scrollContent: {
    paddingHorizontal: layoutTokens.gutter,
  },
  // Intro (tightened, left-aligned)
  intro: {
    alignItems: 'flex-start',
    marginBottom: spacing.lg,
  },
  introIcon: {
    width: 52,
    height: 52,
    borderRadius: radius.full,
    backgroundColor: colors.primary + '18',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.sm,
  },
  introTitle: {
    ...typographyScale.h2,
    ...fonts.bold,
    color: colors.ink,
  },
  introSub: {
    ...typographyScale.body,
    color: colors.muted,
    marginTop: spacing.xs,
  },
  previewLabel: {
    ...typographyScale.caption,
    ...fonts.bold,
    color: colors.ink2,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginBottom: spacing.sm,
  },
  previewWrap: {
    marginBottom: spacing.xl,
  },
  label: {
    ...typographyScale.body,
    ...fonts.semibold,
    color: colors.ink,
    marginTop: spacing.lg,
    marginBottom: spacing.sm,
  },
  inputContent: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
  },
  counter: {
    ...typographyScale.caption,
    color: colors.muted,
    alignSelf: 'flex-end',
    marginTop: spacing.xs,
  },
  input: {
    ...typographyScale.body,
    color: colors.ink,
    paddingVertical: spacing.sm,
  },
  textArea: {
    minHeight: 100,
    lineHeight: 22,
  },
  slugPreview: {
    ...typographyScale.caption,
    ...fonts.medium,
    color: colors.primary,
    marginTop: spacing.xs,
    marginLeft: spacing.xs,
  },
  // Picture
  pictureRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    marginTop: spacing.sm,
  },
  thumbPress: {
    borderRadius: radius.lg,
  },
  thumbContent: {
    width: 56,
    height: 56,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  thumbImg: {
    width: 56,
    height: 56,
    borderRadius: radius.lg,
  },
  pictureActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  removeText: {
    ...typographyScale.caption,
    ...fonts.medium,
    color: colors.muted,
  },
  // Color
  swatchStrip: {
    paddingVertical: spacing.sm,
    paddingRight: spacing.md,
  },
  swatchCol: {
    alignItems: 'center',
    marginRight: spacing.sm,
  },
  swatch: {
    width: 34,
    height: 34,
    borderRadius: radius.full,
    alignItems: 'center',
    justifyContent: 'center',
  },
  swatchSelected: {
    borderWidth: 2,
    borderColor: colors.white,
  },
  swatchLabel: {
    ...typographyScale.caption,
    color: colors.muted,
    marginTop: 4,
  },
  colorNote: {
    ...typographyScale.caption,
    color: colors.muted,
    marginTop: spacing.xs,
  },
  // Token gating
  toggleCard: {
    marginTop: spacing.xl,
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  toggleInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: spacing.md,
  },
  toggleIcon: {
    width: 36,
    height: 36,
    borderRadius: radius.md,
    backgroundColor: colors.primary + '15',
    alignItems: 'center',
    justifyContent: 'center',
  },
  toggleTextWrap: {
    flex: 1,
  },
  toggleTitle: {
    ...typographyScale.body,
    ...fonts.semibold,
    color: colors.ink,
  },
  toggleDesc: {
    ...typographyScale.caption,
    color: colors.muted,
    marginTop: 2,
  },
  // Pinned frosted commit bar (§12 scroll-edge / floating chrome).
  footer: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
  },
  footerContent: {
    paddingHorizontal: layoutTokens.gutter,
    paddingTop: spacing.md,
  },
  // ── Eligibility gate ──
  centerFill: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.md,
    paddingHorizontal: layoutTokens.gutter,
  },
  gateCheckingText: {
    ...typographyScale.body,
    color: colors.muted,
  },
  gateHeader: {
    alignItems: 'center',
    marginBottom: spacing.xl,
  },
  gateLockCircle: {
    width: 64,
    height: 64,
    borderRadius: radius.full,
    backgroundColor: colors.primary + '18',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.md,
  },
  gateTitle: {
    ...typographyScale.h2,
    ...fonts.bold,
    color: colors.ink,
    textAlign: 'center',
    letterSpacing: -0.3,
  },
  gateSub: {
    ...typographyScale.body,
    color: colors.muted,
    textAlign: 'center',
    marginTop: spacing.sm,
    lineHeight: 21,
  },
  gateCard: {
    marginBottom: spacing.xl,
  },
  gateOrRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    marginVertical: spacing.md,
  },
  gateOrLine: {
    flex: 1,
    height: 1,
    backgroundColor: colors.cardBorder,
  },
  gateOrText: {
    ...typographyScale.caption,
    ...fonts.bold,
    color: colors.muted,
    letterSpacing: 0.6,
  },
  gateActions: {
    gap: spacing.md,
  },
  gateRequestedCard: {
    backgroundColor: colors.primary + '10',
  },
  gateRequestedRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  gateRequestedText: {
    ...typographyScale.caption,
    ...fonts.medium,
    color: colors.ink2,
    flex: 1,
    lineHeight: 18,
  },
  // Criterion row
  criterionRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.md,
  },
  criterionCheck: {
    width: 28,
    height: 28,
    borderRadius: radius.full,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 1,
  },
  criterionCheckMet: {
    backgroundColor: colors.success,
  },
  criterionCheckUnmet: {
    backgroundColor: colors.faint + '40',
    borderWidth: 1,
    borderColor: colors.cardBorder,
  },
  criterionTextWrap: {
    flex: 1,
  },
  criterionTitle: {
    ...typographyScale.body,
    ...fonts.semibold,
    color: colors.ink,
  },
  criterionTitleMet: {
    color: colors.ink,
  },
  criterionDetail: {
    ...typographyScale.caption,
    color: colors.muted,
    marginTop: 2,
    lineHeight: 17,
  },
});
